import { MRT_Row, MRT_TableInstance } from "material-react-table";
import { Box, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BookingProperty } from "../FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";

const ALL_TYPE_KEYS = ["usedForWorkplace", "usedForZone", "usedForFleet"] as const;
const LABELS: Record<(typeof ALL_TYPE_KEYS)[number], string> = {
  usedForWorkplace: "Workplace",
  usedForZone: "Room",
  usedForFleet: "Drawer_Fleet"
};

type BookingPropertyTypeSwitchesProps = {
  table: MRT_TableInstance<BookingProperty>;
  row: MRT_Row<BookingProperty>;
  hasFleet?: boolean;
};

export function BookingPropertyTypeSwitches({
  table,
  row,
  hasFleet = true
}: Readonly<BookingPropertyTypeSwitchesProps>) {
  const { t } = useTranslation();
  const keys = hasFleet ? ALL_TYPE_KEYS : (["usedForWorkplace", "usedForZone"] as const);

  const { setCreatingRow, setEditingRow, getState } = table;
  const { creatingRow, editingRow } = getState();
  const isCreating = creatingRow?.id === row.id;

  const handleChange = (accessorKey: (typeof ALL_TYPE_KEYS)[number], next: boolean) => {
    const cache = (row as MRT_Row<BookingProperty> & { _valuesCache?: Record<string, unknown> })
      ._valuesCache ?? { ...row.original };
    const updatedRow = {
      ...row,
      _valuesCache: { ...cache, [accessorKey]: next }
    } as MRT_Row<BookingProperty>;
    if (isCreating) {
      setCreatingRow(updatedRow);
    } else if (editingRow?.id === row.id) {
      setEditingRow(updatedRow);
    }
  };

  const getValue = (accessorKey: (typeof ALL_TYPE_KEYS)[number]): boolean => {
    const cache = (row as MRT_Row<BookingProperty> & { _valuesCache?: Record<string, unknown> })
      ._valuesCache;
    if (cache && accessorKey in cache) return !!cache[accessorKey];
    return !!row.original[accessorKey];
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {t("_propertiesPerBookingType_typeColumn")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          alignItems: "center",
          flexWrap: "nowrap"
        }}
      >
        {keys.map(accessorKey => (
          <Box key={accessorKey} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: 14 }}>{t(LABELS[accessorKey])}</Typography>
            <Switch
              data-testid={`booking-property-edit-${accessorKey}`}
              size="small"
              checked={getValue(accessorKey)}
              onChange={(_e, checked) => handleChange(accessorKey, checked)}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
